-- | Step 3 attachments widget for a document.
-- |
-- | Renders the attachment list and handles upload/delete actions.
-- | Upload and delete requests are sent to the app runtime.
module Widgets.Attachments where

import Common

import API.Attachments (Attachment, AttachmentInput)
import API.Types (DocumentID, ID)
import Concur.React.DOM as D
import Concur.React.Props as P
import Data.Array (catMaybes)
import Internal.Luxon as DT
import Internal.Util (justIf)
import Web.File.File as File
import Widgets.Confirmation (confirmModal)
import Widgets.Upload (getUploadFile)

type AttachmentsBase r =
    { id :: String -- ^ ID of this widget
    , formId :: DocumentID -- ^ ID of the form we're attachmenting on.
    , attachments :: Array Attachment
    | r
    }

type Attachments = AttachmentsBase ( description :: String )

data AttachmentFormResult
    = UploadAttachment
    | DeleteAttachment ID
    | StateChange Attachments

data AttachmentRequest
    = CreateAttachment AttachmentInput
    | RemoveAttachment ID

data AttachmentsResult
    = AttachmentsChanged Attachments
    | AttachmentsRequest AttachmentRequest

initAttachments :: AttachmentsBase () -> Attachments
initAttachments { id, formId, attachments } =
    { id, formId, attachments, description: "" }

runAttachments :: Attachments -> Widget HTML AttachmentsResult
runAttachments state = handleResult state =<< renderAttachments state

handleResult :: Attachments -> AttachmentFormResult -> Widget HTML AttachmentsResult
handleResult _ (StateChange state') = pure $ AttachmentsChanged state'
handleResult state (DeleteAttachment id) = do
    conf <- confirmModal (renderAttachments state)
        (lookupTranslation "confirm-delete-attachment-title")
        (lookupTranslation "confirm-delete-attachment-primary-button")
        (lookupTranslation "confirm-delete-attachment")
    if conf
        then pure $ AttachmentsRequest $ RemoveAttachment id
        else pure $ AttachmentsChanged state
handleResult state UploadAttachment = do
    file <- liftEffect $ getUploadFile $ uploadId state
    let name = if state.description == "" then File.name file else state.description
    pure $ AttachmentsRequest $ CreateAttachment { description: name, file }

renderAttachments :: Attachments -> Widget HTML AttachmentFormResult
renderAttachments state@{ id, attachments } = D.details
    [ P.key id, P._id id, P.open $ length attachments > 0 ]
    [ D.summary'
        [ D.h1
            [ P.className "attachment-section-heading" ]
            [ D.text $ lookupTranslation "attachment-section" ]
        ]
    , D.div [ P.className "attachment-section" ]
        [ D.div [ P.className "attachment-list" ] $ map (renderAttachment state) attachments
        , attachmentForm state
        ]
    ]

renderAttachment :: Attachments -> Attachment -> Widget HTML AttachmentFormResult
renderAttachment _ att = D.div
    [ P.className "attachment" ]
    [ D.a
        [ P.className "attachment-label" , P.href att.url, P.target "_blank" ]
        [ D.text $ if att.description == "" then att.path else att.description ]
    , D.div [ P.className "attachment-meta" ]
        [ D.div [ P.className "attachment-by" ]
            [ D.text att.user.name ]
        , D.div [ P.className "attachment-time" ]
            [ D.text $ DT.toRelative att.created_at ]
        ]
    , D.div [ P.className "attachment-buttons" ] $ catMaybes
        [ justIf (att.can.delete) $ D.button
            [ P.className "attachment-delete-button btn"
            , DeleteAttachment att.id <$ P.onClick
            ]
            [ D.text $ lookupTranslation "attachment-delete" ]
        ]
    ]

attachmentForm :: Attachments -> Widget HTML AttachmentFormResult
attachmentForm state = D.div
    [ P.className "attachment-form" ]
    [ D.p [ P.className "attachment-form-header" ] [ D.text $ lookupTranslation "attachment-form" ]
    , D.input
        [ P._type "file"
        , P._id $ uploadId state
        , UploadAttachment <$ P.onChange
        ]
    ]

uploadId :: Attachments -> String
uploadId state = state.id <> "-attachment-upload"
